import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';
import BottomNav from '../components/BottomNav';

import btcIcon from '../assets/coins/btc.png';
import ethIcon from '../assets/coins/eth.png';
import usdtIcon from '../assets/coins/usdt.png';

const coinIcons = {
  BTC: btcIcon,
  ETH: ethIcon,
  USDT: usdtIcon,
};

const UserMarkets = () => {
  const [search, setSearch] = useState('');
  const [activePopup, setActivePopup] = useState(null);
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await api.get('/api/trading/prices');

        const prices = response.data?.data?.prices || [];

        const formattedData = prices.map((coin) => ({
          symbol: `${coin.symbol}/USDT`,
          price: coin.currentPrice,
          change: coin.priceChange24h,
          icon: coin.icon || coinIcons[coin.symbol] || btcIcon,
          name: coin.name,
        }));

        setMarketData(formattedData);
      } catch (error) {
        toast.error('Failed to load market prices', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredData = marketData.filter((coin) =>
    coin.symbol.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      style={{
        background: '#FCFCFC',
        minHeight: '100vh',
        paddingTop: '20px',
        paddingBottom: '100px',
      }}
    >
      <Container
        className='p-3'
        style={{
          maxWidth: isMobile ? '100%' : '85%',
        }}
      >
        {/* Header */}
        <div className='mb-3'>
          <h4 className='fw-bold' style={{ color: '#333' }}>
            Markets
          </h4>
        </div>

        {/* Search Bar */}
        <div className='position-relative mb-3'>
          <i
            className='bi bi-search position-absolute'
            style={{
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#999',
              fontSize: '1rem',
              zIndex: 2,
            }}
          ></i>
          <Form.Control
            type='text'
            placeholder='Search cryptocurrency...'
            className='mb-0'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              borderRadius: '12px',
              padding: '12px 16px 12px 42px',
              border: '1px solid #e0e0e0',
              fontSize: '0.95rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
          />
        </div>

        {/* Coin List */}
        <Row className='g-2'>
          {loading ? (
            <Col xs={12} className='text-center py-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </Col>
          ) : filteredData.length === 0 ? (
            <Col xs={12} className='text-center py-4'>
              <p className='text-muted'>No cryptocurrencies found</p>
            </Col>
          ) : (
            filteredData.map((coin, index) => (
              <Col xs={12} key={index} className='position-relative'>
                <div
                  className='d-flex align-items-center justify-content-between bg-white rounded p-3 shadow-sm'
                  onClick={() =>
                    setActivePopup(activePopup === index ? null : index)
                  }
                  style={{
                    cursor: 'pointer',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    border: '1px solid #f0f0f0',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className='d-flex align-items-center gap-2'>
                    <img
                      src={coin.icon}
                      alt={coin.symbol}
                      width={32}
                      height={32}
                      className='coin-icon me-2'
                    />
                    <div>
                      <strong style={{ fontSize: '1rem', color: '#333' }}>
                        {coin.symbol}
                      </strong>
                      <div
                        className='text-muted small'
                        style={{ fontSize: '0.85rem' }}
                      >
                        ${coin.price.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`fw-bold ${
                      coin.change >= 0 ? 'text-success' : 'text-danger'
                    }`}
                    style={{ fontSize: '0.95rem' }}
                  >
                    {coin.change >= 0 ? '+' : ''}
                    {coin.change.toFixed(2)}%
                  </div>
                </div>

                {/* Popup */}
                {activePopup === index && (
                  <div
                    className='position-absolute top-0 end-0 bg-white p-3 rounded shadow-lg'
                    style={{
                      width: '170px',
                      zIndex: 1100,
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <strong className='small' style={{ color: '#333' }}>
                        {coin.symbol}
                      </strong>
                      <button
                        className='btn btn-sm p-0'
                        onClick={(e) => {
                          e.stopPropagation();
                          setActivePopup(null);
                        }}
                        style={{
                          fontSize: '1.5rem',
                          lineHeight: '1',
                          color: '#999',
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div
                      className='text-muted small mb-3'
                      style={{ fontSize: '0.8rem' }}
                    >
                      Price: ${coin.price.toLocaleString()}
                    </div>

                    <Link to='/option' className='text-decoration-none'>
                      <button
                        className='btn btn-sm w-100 mb-2'
                        style={{
                          background:
                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                        }}
                      >
                        Option
                      </button>
                    </Link>

                    <Link to='/exchange' className='text-decoration-none'>
                      <button
                        className='btn btn-outline-secondary btn-sm w-100'
                        style={{
                          borderRadius: '8px',
                          padding: '8px',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                        }}
                      >
                        Exchange
                      </button>
                    </Link>
                  </div>
                )}
              </Col>
            ))
          )}
        </Row>

        {/* Bottom Navigation */}
        <BottomNav />
      </Container>
    </div>
  );
};

export default UserMarkets;
